import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { FaSpinner } from "react-icons/fa";

import { fetchAvailability } from "../../../../api/resort-booking/resort.service";
import Export from "./Exports/Export";

const getSessionUser = () => {
  try {
    return JSON.parse(sessionStorage.getItem("userDetails") || "{}");
  } catch (error) {
    return {};
  }
};

const getAvailabilityTone = (available, total) => {
  if (total <= 0) return { background: "#f1f3f5", color: "#6c757d" };
  if (available <= 0) return { background: "#ffe3e3", color: "#c92a2a" };
  if (available <= Math.ceil(total * 0.25)) {
    return { background: "#fff3bf", color: "#9c6500" };
  }

  return { background: "#d3f9d8", color: "#2b8a3e" };
};

const calendarTableStyle = {
  borderCollapse: "separate",
  borderSpacing: 0,
};

const getStickyColumnStyle = (minWidth = 260, isHeader = false) => ({
  position: "sticky",
  left: 0,
  minWidth,
  background: isHeader ? "#f8f9fa" : "#fff",
  zIndex: isHeader ? 5 : 4,
  boxShadow: "8px 0 14px -14px rgba(0,0,0,0.45)",
});

const Dashboard = () => {
  const userDetails = useMemo(getSessionUser, []);
  const scopedHotelId = userDetails.hotelId || "";
  const hasScopedHotel = Boolean(scopedHotelId);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedHotelId, setSelectedHotelId] = useState("");
  const [startDate, setStartDate] = useState(moment().format("YYYY-MM-DD"));
  const [endDate, setEndDate] = useState(
    moment().add(29, "days").format("YYYY-MM-DD")
  );
  const [availability, setAvailability] = useState({
    hotels: [],
    roomPlans: [],
    roomsCount: 0,
  });
  const tableRef = useRef(null);

  const days = useMemo(() => {
    const start = moment(startDate, "YYYY-MM-DD", true);
    const end = moment(endDate, "YYYY-MM-DD", true);
    if (!start.isValid() || !end.isValid() || end.isBefore(start, "day")) {
      return [];
    }
    const range = [];
    const count = end.diff(start, "days") + 1;
    for (let i = 0; i < count; i += 1) {
      range.push(start.clone().add(i, "days").format("YYYY-MM-DD"));
    }
    return range;
  }, [startDate, endDate]);

  const selectedHotel = useMemo(
    () => availability.hotels.find((hotel) => hotel.id === selectedHotelId),
    [availability.hotels, selectedHotelId]
  );

  const selectedHotelRoomPlans = useMemo(
    () =>
      availability.roomPlans.filter(
        (roomPlan) => roomPlan.hotelId === selectedHotelId
      ),
    [availability.roomPlans, selectedHotelId]
  );

  const visibleDateLabel = useMemo(() => {
    if (!days.length) return "";
    return `${moment(days[0]).format("DD MMM YYYY")} - ${moment(
      days[days.length - 1]
    ).format("DD MMM YYYY")}`;
  }, [days]);

  const loadAvailability = useCallback(async () => {
    if (!days.length) {
      setAvailability({ hotels: [], roomPlans: [], roomsCount: 0 });
      setError("Please select a valid date range.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");

    try {
      const snapshot = await fetchAvailability({
        startDate,
        endDate,
        hotelId: hasScopedHotel ? scopedHotelId : "",
      });

      setAvailability({
        hotels: snapshot.hotels || [],
        roomPlans: snapshot.roomPlans || [],
        roomsCount: snapshot.roomsCount || 0,
      });
    } catch (loadError) {
      console.error("Resorts dashboard availability load error:", loadError);
      setError("Failed to load resort availability.");
    } finally {
      setLoading(false);
    }
  }, [days, endDate, hasScopedHotel, scopedHotelId, startDate]);

  useEffect(() => {
    loadAvailability();
  }, [loadAvailability]);

  useEffect(() => {
    if (
      selectedHotelId &&
      !availability.hotels.some((hotel) => hotel.id === selectedHotelId)
    ) {
      setSelectedHotelId("");
    }
  }, [availability.hotels, selectedHotelId]);

  const exportFilename = useMemo(() => {
    if (selectedHotel?.name) {
      const slug = selectedHotel.name
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "");
      return `resort-availability-${slug || "hotel"}`;
    }
    return "resort-availability-all-hotels";
  }, [selectedHotel]);

  const totalCapacity = availability.hotels.reduce(
    (sum, hotel) => sum + Number(hotel.total || 0),
    0
  );
  const firstVisibleDay = days[0];
  const soldOnFirstVisibleDay = availability.hotels.reduce((sum, hotel) => {
    const dayStats = hotel.daily.find((day) => day.date === firstVisibleDay);
    return sum + Number(dayStats?.sold || 0);
  }, 0);

  const renderAvailabilityCell = (stats) => {
    const tone = getAvailabilityTone(stats.available, stats.total);

    return (
      <div
        className="rounded p-2 text-center"
        style={{
          minWidth: 118,
          background: tone.background,
          color: tone.color,
          lineHeight: 1.35,
        }}
      >
        <div className="fw-bold">Available: {stats.available}</div>
        <small>Total: {stats.total}</small>
        <br />
        <small>Sold: {stats.sold}</small>
      </div>
    );
  };

  return (
    <div className="container-fluid">
      <div className="d-flex flex-wrap justify-content-between align-items-end mb-4 gap-3">
        <div>
          <h3 className="mb-1">Resorts Availability Calendar</h3>
          <div className="text-muted">
            {selectedHotel
              ? `Room-wise availability for ${selectedHotel.name}`
              : hasScopedHotel
              ? "Hotel-wise availability for your assigned resort"
              : "Hotel-wise availability for all resorts"}
          </div>
          {selectedHotel && (
            <button
              type="button"
              className="btn btn-link px-0 mt-2"
              onClick={() => setSelectedHotelId("")}
            >
              &larr; Back to All Hotels
            </button>
          )}
        </div>

        <div className="d-flex flex-wrap align-items-end gap-2">
          <Export
            tableRef={tableRef}
            viewMode={selectedHotel ? "roomPlans" : "hotels"}
            hotels={availability.hotels}
            roomPlans={selectedHotelRoomPlans}
            days={days}
            filename={exportFilename}
            disabled={loading || Boolean(error)}
          />
          <div>
            <label className="form-label mb-1 small text-muted">Start Date</label>
            <input
              type="date"
              className="form-control"
              value={startDate}
              onChange={(event) => setStartDate(event.target.value)}
            />
          </div>
          <div>
            <label className="form-label mb-1 small text-muted">End Date</label>
            <input
              type="date"
              className="form-control"
              value={endDate}
              onChange={(event) => setEndDate(event.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="row mb-4">
        <div className="col-xl-3 col-sm-6 mb-3">
          <div className="card h-100">
            <div className="card-body">
              <div className="text-muted">Hotels</div>
              <h3 className="mb-0">{availability.hotels.length}</h3>
            </div>
          </div>
        </div>
        <div className="col-xl-3 col-sm-6 mb-3">
          <div className="card h-100">
            <div className="card-body">
              <div className="text-muted">Approved Rooms</div>
              <h3 className="mb-0">{availability.roomsCount}</h3>
            </div>
          </div>
        </div>
        <div className="col-xl-3 col-sm-6 mb-3">
          <div className="card h-100">
            <div className="card-body">
              <div className="text-muted">Plan Capacity</div>
              <h3 className="mb-0">{totalCapacity}</h3>
            </div>
          </div>
        </div>
        <div className="col-xl-3 col-sm-6 mb-3">
          <div className="card h-100">
            <div className="card-body">
              <div className="text-muted">
                Sold on {firstVisibleDay ? moment(firstVisibleDay).format("DD MMM") : "-"}
              </div>
              <h3 className="mb-0">{soldOnFirstVisibleDay}</h3>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h4 className="card-title mb-1">
            {selectedHotel
              ? selectedHotel.name
              : hasScopedHotel
              ? "Assigned Hotel"
              : "All Hotels"}
          </h4>
          <div className="text-muted">{visibleDateLabel}</div>
        </div>

        <div className="card-body position-relative">
          {loading && (
            <div
              className="position-absolute w-100 h-100 d-flex align-items-center justify-content-center"
              style={{
                inset: 0,
                background: "rgba(255,255,255,0.85)",
                zIndex: 10,
              }}
            >
              <div className="text-center">
                <FaSpinner className="spin mb-2" style={{ fontSize: 28 }} />
                <div>Loading availability...</div>
              </div>
            </div>
          )}

          {error && <div className="alert alert-danger">{error}</div>}

          {!loading && !error && !selectedHotel && (
            <>
              <div
                ref={tableRef}
                className="table-responsive"
                style={{ overflowX: "auto" }}
              >
              <table
                className="table table-bordered align-middle mb-0"
                style={calendarTableStyle}
              >
                <thead>
                  <tr>
                    <th style={getStickyColumnStyle(260, true)}>Hotel</th>
                    {days.map((day) => (
                      <th key={day} className="text-center" style={{ minWidth: 138 }}>
                        {moment(day).format("DD MMM YYYY")}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {availability.hotels.length ? (
                    availability.hotels.map((hotel) => (
                      <tr key={hotel.id}>
                        <td style={getStickyColumnStyle(260)}>
                          <button
                            type="button"
                            className="btn btn-link p-0 text-start fw-bold"
                            onClick={() => setSelectedHotelId(hotel.id)}
                          >
                            {hotel.name || "Unnamed Hotel"}
                          </button>
                          <div className="text-muted small">
                            {hotel.roomsCount} rooms, {hotel.plansCount} plans
                          </div>
                        </td>
                        {hotel.daily.map((stats) => (
                          <td key={stats.date}>{renderAvailabilityCell(stats)}</td>
                        ))}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={days.length + 1} className="text-center py-4">
                        No approved hotels found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              </div>
            </>
          )}

          {!loading && !error && selectedHotel && (
            <>
              <div className="text-muted small mb-2">
                Scroll horizontally to view more dates. Room names stay pinned on
                the left.
              </div>
              <div
                ref={tableRef}
                className="table-responsive"
                style={{ overflowX: "auto" }}
              >
              <table
                className="table table-bordered align-middle mb-0"
                style={calendarTableStyle}
              >
                <thead>
                  <tr>
                    <th style={getStickyColumnStyle(280, true)}>
                      Room / Rate Plan
                    </th>
                    {days.map((day) => (
                      <th key={day} className="text-center" style={{ minWidth: 138 }}>
                        {moment(day).format("DD MMM YYYY")}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {selectedHotelRoomPlans.length ? (
                    selectedHotelRoomPlans.map((roomPlan) => (
                      <tr key={`${roomPlan.roomId}-${roomPlan.planId}`}>
                        <td style={getStickyColumnStyle(280)}>
                          <div className="fw-bold">{roomPlan.roomName}</div>
                          <div className="text-muted small">{roomPlan.planName}</div>
                        </td>
                        {roomPlan.daily.map((stats) => (
                          <td key={stats.date}>{renderAvailabilityCell(stats)}</td>
                        ))}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={days.length + 1} className="text-center py-4">
                        No approved room plans found for this hotel.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;